import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PanchTatvaButton from '../components/PanchTatvaButton';

const faqs = [
  {
    category: 'Orders & Payments',
    items: [
      {
        q: 'How do I place an order?',
        a: 'Browse our shop, add products to your cart, and proceed to checkout. You\'ll need to log in with your Google account to complete the purchase.',
      },
      {
        q: 'What payment methods do you accept?',
        a: 'We accept all major UPI apps (GPay, PhonePe, Paytm), credit/debit cards (Visa, Mastercard, RuPay), net banking, and popular wallets — all powered by Razorpay.',
      },
      {
        q: 'Is my payment secure?',
        a: 'Absolutely. Payments are processed by Razorpay, a PCI DSS-compliant payment gateway. We never store your card details.',
      },
      {
        q: 'I was charged but didn\'t receive an order confirmation. What do I do?',
        a: 'This is rare but can happen due to network issues. Please email support@panch-tatva.co.in with your transaction ID and we\'ll resolve it within 24 hours.',
      },
    ],
  },
  {
    category: 'Shipping & Delivery',
    items: [
      {
        q: 'How long does delivery take?',
        a: 'Standard delivery takes 5–7 business days. Express delivery (available in select cities) takes 2–3 business days.',
      },
      {
        q: 'Do you offer free shipping?',
        a: 'Yes! Orders above ₹1,000 qualify for free standard shipping across India.',
      },
      {
        q: 'How can I track my order?',
        a: 'Once dispatched, you\'ll receive a tracking number via email. You can also view your order status in the Order History section of your account.',
      },
      {
        q: 'Do you ship internationally?',
        a: 'Not yet, but we\'re working on it! Currently we ship to all major locations across India.',
      },
    ],
  },
  {
    category: 'Products',
    items: [
      {
        q: 'Are your products natural and handcrafted?',
        a: 'Yes. Every Panch Tatva product is handcrafted using natural ingredients inspired by the five elements. We avoid synthetic fragrances and harsh chemicals.',
      },
      {
        q: 'Are the products suitable for sensitive skin?',
        a: 'Most of our products are formulated to be gentle. However, individual sensitivities vary. We recommend doing a patch test before full use.',
      },
      {
        q: 'What is the shelf life of your products?',
        a: 'Products typically have a shelf life of 12–18 months when stored in a cool, dry place away from direct sunlight.',
      },
    ],
  },
  {
    category: 'Returns & Refunds',
    items: [
      {
        q: 'What is your return policy?',
        a: 'We accept returns within 7 days of delivery for unopened products or if the product is damaged/wrong. See our full Return Policy for details.',
      },
      {
        q: 'How long does a refund take?',
        a: 'Refunds are processed within 5–7 business days after we receive and inspect the returned product.',
      },
      {
        q: 'Can I cancel my order?',
        a: 'Yes, orders can be cancelled within 12 hours of placement if they haven\'t been dispatched. Email us immediately with your order ID.',
      },
    ],
  },
];

const FAQItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-gray-200 last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full text-left py-4 flex justify-between items-start gap-4 hover:text-secondary transition"
      >
        <span className="font-medium text-sm md:text-base text-headingText">{q}</span>
        <span className="text-secondary text-lg leading-none mt-0.5 flex-shrink-0">
          {open ? '−' : '+'}
        </span>
      </button>
      {open && (
        <p className="pb-4 text-sm md:text-base text-bodyText leading-relaxed">{a}</p>
      )}
    </div>
  );
};

export default function FAQ() {
  const navigate = useNavigate();
  return (
    <div className="w-full max-w-4xl mx-auto px-4 md:px-6 py-12 md:py-20">
      {/* Header */}
      <div className="text-center mb-10 md:mb-14">
        <p className="text-secondary font-medium tracking-widest text-xs md:text-sm uppercase mb-3">Support</p>
        <h1 className="text-3xl md:text-5xl font-heading text-headingText mb-4">
          Frequently Asked Questions
        </h1>
        <p className="text-sm md:text-base text-bodyText opacity-80 max-w-xl mx-auto">
          Find answers to the most common questions about orders, products, shipping, and more.
        </p>
      </div>

      <div className="space-y-6 md:space-y-8">
        {faqs.map((section) => (
          <div key={section.category} className="bg-cardBg rounded-2xl p-6 md:p-8 shadow-sm">
            <h2 className="text-lg md:text-xl font-heading text-secondary mb-4">{section.category}</h2>
            <div>
              {section.items.map((item) => (
                <FAQItem key={item.q} q={item.q} a={item.a} />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Still have questions? */}
      <div className="mt-12 text-center bg-cardBg rounded-2xl p-8 md:p-10">
        <p className="text-xl md:text-2xl font-heading text-headingText mb-2">Still have questions?</p>
        <p className="text-sm md:text-base text-bodyText mb-6">
          Our support team is happy to help. Reach out and we'll get back to you within 24 hours.
        </p>
        <PanchTatvaButton onClick={() => navigate('/contact')}>
          Contact Us
        </PanchTatvaButton>
      </div>

      <div className="mt-8 text-center">
        <PanchTatvaButton onClick={() => navigate(-1)} variant="outline">
          ← Go Back
        </PanchTatvaButton>
      </div>
    </div>
  );
}
